import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Heart, Key, Menu, X, Sparkles, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/LanguageContext";
import Logo from "@/components/Logo";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, language, toggleLanguage } = useLanguage();
  
  const navLinks = [
    { path: "/", label: t('nav.home') },
    { path: "/properties?listing_type=buy", label: t('nav.buy') },
    { path: "/properties?listing_type=rent", label: t('nav.rent') },
    { path: "/blog", label: t('nav.blog') },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path.split("?")[0]);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur shadow-sm" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <Logo size={44} />
            <span className="font-heading text-2xl font-semibold text-charcoal">Dream House</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.path) ? "text-primary" : "text-charcoal"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/list-property"
              className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
            >
              {t('nav.listProperty')}
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            {/* Language Toggle */}
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 transition-colors text-sm font-medium"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <button className="p-2 hover:bg-secondary rounded-full">
              <Heart className="w-5 h-5 text-charcoal" />
            </button>
            <Link to="/contact">
              <Button className="rounded-full">
                {t('nav.contact')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-secondary text-sm font-medium"
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <button
              className="p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-border pt-4">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium ${
                    isActive(link.path) ? "text-primary" : "text-charcoal"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/list-property"
                className="text-sm font-medium text-primary"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.listProperty')}
              </Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full rounded-full">{t('nav.contact')}</Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

const PromoBanner = ({ onClose }) => {
  const { t } = useLanguage();
  
  return (
    <div className="bg-gradient-to-r from-primary to-accent text-white py-3 px-6 relative" data-testid="promo-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 text-center">
        <Sparkles className="w-5 h-5 animate-pulse" aria-hidden="true" />
        <p className="font-medium">
          <strong>{t('promoBanner.text')}</strong>
          <Link to="/list-property" className="ml-2 underline hover:no-underline">
            {t('promoBanner.cta')}
          </Link>
        </p>
        <button
          onClick={onClose}
          className="absolute right-4 top-1/2 -translate-y-1/2 hover:opacity-70"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-charcoal text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Logo size={36} />
              <span className="font-heading text-xl font-semibold">Dream House</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-xs">
              <span>🇨🇦</span> {t('footer.proudlyCanadian')}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg font-medium mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              <li><Link to="/" className="hover:text-white">{t('nav.home')}</Link></li>
              <li><Link to="/properties?listing_type=buy" className="hover:text-white">{t('nav.buy')}</Link></li>
              <li><Link to="/properties?listing_type=rent" className="hover:text-white">{t('nav.rent')}</Link></li>
              <li><Link to="/blog" className="hover:text-white">{t('nav.blog')}</Link></li>
              <li><Link to="/list-property" className="hover:text-white">{t('nav.listProperty')}</Link></li>
              <li><Link to="/contact" className="hover:text-white">{t('nav.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg font-medium mb-4">{t('footer.contactUs')}</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              <li>(438) 342-2030</li>
              <li>contact@dreamhousecanada.org</li>
              <li>123 Rue Sainte-Catherine</li>
              <li>Montréal, QC H3B 1A7</li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg font-medium mb-4">{t('footer.popularCities')}</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              <li><Link to="/properties?city=Montreal" className="hover:text-white">Montréal</Link></li>
              <li><Link to="/properties?city=Toronto" className="hover:text-white">Toronto</Link></li>
              <li><Link to="/properties?city=Vancouver" className="hover:text-white">Vancouver</Link></li>
              <li><Link to="/properties?city=Ottawa" className="hover:text-white">Ottawa</Link></li>
              <li><Link to="/properties?city=Calgary" className="hover:text-white">Calgary</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/50 text-sm">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  const [showBanner, setShowBanner] = useState(true);

  return (
    <>
      {showBanner && <PromoBanner onClose={() => setShowBanner(false)} />}
      <Header />
      <main className="pt-20">
        <Outlet />
      </main>
      <Footer />
    </>
  );
};

export default Layout;
